import { isValidCodeOrSerial } from '@/common/utils/scan-code-validator';
import { Scanner } from '@/components/scanner';
import { MobileContainer } from '@/components/workflow/mobile-container';
import { MobileHeader } from '@/components/workflow/mobile-header';
import { WorkflowFooter } from '@/components/workflow/workflow-footer';
import { cleanCode } from '@/utils';
import { Button, Drawer, Flex, Text, TextInput, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { useBaleOutboundContext } from '../hook';
import styles from '../styles.module.css';

/**
 * Displays scanned materials with animated counts
 */
const ScannedMaterialsDisplay: React.FC<{
	materials: Array<{
		value: string;
		name: string;
		emoji: string;
		count: number;
	}>;
}> = ({ materials }) => (
	<motion.div
		initial={{ opacity: 0, y: 20 }}
		animate={{ opacity: 1, y: 0 }}
		exit={{ opacity: 0, y: 20 }}
		transition={{ duration: 0.3, ease: 'easeOut' }}
		style={{ width: '100%' }}
	>
		<Flex px="md" py="xs" w="100%" align="center" justify="center" gap="xs">
			<AnimatePresence>
				{materials.map((material, index) => (
					<motion.div
						key={material.value}
						initial={{ opacity: 0, scale: 0.8 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.8 }}
						transition={{
							duration: 0.3,
							delay: index * 0.1,
							ease: 'easeOut',
						}}
						style={{ flex: 1 }}
					>
						<Flex
							gap="xs"
							align="center"
							justify="center"
							p="sm"
							className={styles.statsCardAccepted}
						>
							<Text size="lg">{material.emoji}</Text>
							<Flex direction="column" align="center" gap={2}>
								<motion.div
									key={material.count}
									initial={{ scale: 1.2 }}
									animate={{ scale: 1 }}
									transition={{ duration: 0.2 }}
								>
									<Text size="lg" fw={700} c="green" lh={1}>
										{material.count}
									</Text>
								</motion.div>
								<Text size="xs" c="green" fw={500} lh={1}>
									{material.name}
								</Text>
							</Flex>
						</Flex>
					</motion.div>
				))}
			</AnimatePresence>
		</Flex>
	</motion.div>
);

/**
 * Manual code entry drawer for adding bales without scanning
 */
const ManualEntryDrawer: React.FC<{
	opened: boolean;
	inputValue: string;
	isValidInput: boolean;
	onClose: () => void;
	onInputChange: (value: string) => void;
	onSubmit: () => void;
}> = ({ opened, inputValue, isValidInput, onClose, onInputChange, onSubmit }) => (
	<Drawer
		opened={opened}
		onClose={onClose}
		position="bottom"
		size="auto"
		withCloseButton={false}
		overlayProps={{ opacity: 0.5, blur: 4 }}
		transitionProps={{ transition: 'slide-up', duration: 300 }}
		zIndex={1200}
	>
		<Flex direction="column" gap="lg" p="md">
			{/* Header */}
			<Flex align="center" justify="space-between">
				<Title order={3}>Enter Code Manually</Title>
				<Button variant="subtle" onClick={onClose} size="sm">
					Cancel
				</Button>
			</Flex>

			{/* Instructions */}
			<Text size="sm" c="dimmed">
				Enter a barcode (e.g., U1AtTi1CLTEtMQ)
			</Text>

			{/* Input Field */}
			<TextInput
				value={inputValue}
				onChange={(e) => onInputChange(e.target.value)}
				placeholder="Enter scan code"
				size="lg"
				autoFocus
				inputMode="text"
				autoCapitalize="none"
				autoCorrect="off"
				spellCheck={false}
				onKeyDown={(e) => {
					if (e.key === 'Enter' && isValidInput) {
						onSubmit();
					}
				}}
				error={inputValue && !isValidInput ? 'Invalid code format' : undefined}
			/>

			{/* Action Buttons */}
			<Flex gap="sm">
				<Button onClick={onClose} variant="outline" flex={1}>
					Cancel
				</Button>
				<Button onClick={onSubmit} disabled={!isValidInput || !inputValue.trim()} flex={1}>
					Submit
				</Button>
			</Flex>
		</Flex>
	</Drawer>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Page for adding bales to an outbound load by scanning or manual entry.
 *
 * This component allows users to:
 * - Scan bale codes to add them to the current load
 * - Manually enter codes to add bales
 * - View real-time feedback of scanned materials grouped by type
 * - Navigate back to the main outbound page
 *
 * The page displays animated cards showing the count of each material type
 * that has been scanned, with smooth transitions when new bales are added.
 */
export const BaleOutboundAddScanPage = () => {
	// ========================================================================
	// CONTEXT AND NAVIGATION
	// ========================================================================

	const { setPage, handleBaleScan, scannedBales } = useBaleOutboundContext();

	// ========================================================================
	// LOCAL STATE
	// ========================================================================

	const [opened, { open, close }] = useDisclosure(false);
	const [inputValue, setInputValue] = useState('');

	// ========================================================================
	// COMPUTED VALUES
	// ========================================================================

	/** Filter scanned bales to only show those with count > 0 */
	const scannedMaterials = scannedBales.filter((bale) => bale.count > 0);

	/** Whether any bales have been scanned */
	const hasScans = scannedMaterials.length > 0;

	/** Validation for manual input */
	const isValidInput = useMemo(() => isValidCodeOrSerial(inputValue), [inputValue]);

	// ========================================================================
	// EVENT HANDLERS
	// ========================================================================

	/**
	 * Handle manual code submission
	 */
	const handleManualSubmit = () => {
		if (isValidInput && inputValue.trim()) {
			handleBaleScan('ADD', inputValue.trim());
			setInputValue('');
			close();
		}
	};

	/**
	 * Handle closing the manual entry drawer
	 */
	const handleCloseSheet = () => {
		setInputValue('');
		close();
	};

	/**
	 * Handle back navigation to facility home
	 */
	const handleBack = () => {
		setPage('ROOT');
	};

	/**
	 * Handle completion of scanning process
	 */
	const handleDone = () => {
		setPage('ROOT');
	};

	// ========================================================================
	// RENDER
	// ========================================================================

	return (
		<MobileContainer
			footer={
				<WorkflowFooter
					secondaryContent={hasScans && <ScannedMaterialsDisplay materials={scannedMaterials} />}
				>
					<Button size="lg" onClick={open} fullWidth>
						Enter Code
					</Button>
					<Button size="lg" onClick={handleDone} variant="filled" fullWidth>
						Done
					</Button>
				</WorkflowFooter>
			}
			header={<MobileHeader title="Add Bales" onBack={handleBack} />}
		>
			{/* Scanner Component */}
			<Scanner onScan={(code) => handleBaleScan('ADD', cleanCode(code))} paused={opened} />

			{/* Manual Code Entry Drawer */}
			<ManualEntryDrawer
				opened={opened}
				inputValue={inputValue}
				isValidInput={isValidInput}
				onClose={handleCloseSheet}
				onInputChange={setInputValue}
				onSubmit={handleManualSubmit}
			/>
		</MobileContainer>
	);
};
